import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Badge,
  Button,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import {
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiEye,
  FiGrid,
  FiPlay,
  FiRefreshCw,
  FiShuffle,
  FiUsers,
  FiZap,
} from "react-icons/fi";
import AuthContext from "../context/AuthContext";
import "./DrawManagement.css";

const API_URL = process.env.REACT_APP_API_URL;

const todayString = () => new Date().toISOString().slice(0, 10);

const getErrorMessage = (error, fallback) => {
  if (typeof error?.response?.data === "string") return error.response.data;
  if (error?.response?.data?.message) return error.response.data.message;
  return fallback;
};

const formatDate = (date) => {
  if (!date) return "Chưa có ngày";
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString("vi-VN");
};

const formatSerieLabel = (name) => (name ? name.replace(/^Serie/i, "Série") : "");

const DrawManagement = () => {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";

  const [seasons, setSeasons] = useState([]);
  const [levels, setLevels] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("");
  const [drawState, setDrawState] = useState(null);
  const [stateMissing, setStateMissing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [playDate, setPlayDate] = useState(todayString());
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [selectedRoundId, setSelectedRoundId] = useState("");
  const [availableNumbers, setAvailableNumbers] = useState(null);
  const [lastPick, setLastPick] = useState(null);
  const [allowTwoRoundAhead, setAllowTwoRoundAhead] = useState(false);

  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [seasonsRes, levelsRes] = await Promise.all([
        axios.get(`${API_URL}/seasons`),
        axios.get(`${API_URL}/team-levels`),
      ]);
      const seasonsData = seasonsRes.data || [];
      const levelsData = levelsRes.data || [];
      setSeasons(seasonsData);
      setLevels(levelsData);

      if (seasonsData.length > 0) {
        setSelectedSeason((current) => current || seasonsData[0].id);
      }

      if (isManager && user?.teamId) {
        const teamRes = await axios.get(`${API_URL}/teams/${user.teamId}`);
        setSelectedLevel(teamRes.data.level_team_id);
      } else if (levelsData.length > 0) {
        const serieA = levelsData.find(
          (level) => level.name?.toLowerCase() === "serie a",
        );
        setSelectedLevel((current) => current || (serieA || levelsData[0]).id);
      }
    } catch (err) {
      setError(getErrorMessage(err, "Không thể tải dữ liệu bốc thăm."));
    } finally {
      setLoading(false);
    }
  }, [isManager, user?.teamId]);

  const fetchState = useCallback(async () => {
    if (!selectedSeason || !selectedLevel || (!isAdmin && !isManager)) return;
    setLoading(true);
    setError("");
    setStateMissing(false);
    try {
      const res = await axios.get(`${API_URL}/schedules/draw/state`, {
        params: {
          season_id: selectedSeason,
          level_team_id: selectedLevel,
        },
      });
      setDrawState(res.data);
      setStateMissing(false);
      if (res.data?.teams?.length) {
        setSelectedTeamId((current) => {
          const teamStillExists = res.data.teams.some(
            (team) => String(team.id) === String(current),
          );
          if (current && teamStillExists) return current;
          const managerTeam = res.data.teams.find(
            (team) => String(team.id) === String(user?.teamId),
          );
          return (managerTeam || res.data.teams[0]).id;
        });
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setDrawState(null);
        setStateMissing(true);
      } else {
        setError(getErrorMessage(err, "Không thể tải trạng thái bốc thăm."));
      }
    } finally {
      setLoading(false);
    }
  }, [selectedSeason, selectedLevel, isAdmin, isManager, user?.teamId]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  const currentRound = useMemo(() => {
    if (!drawState?.rounds?.length) return null;
    const selectedRound = drawState.rounds.find(
      (round) => String(round.round_id) === String(selectedRoundId),
    );
    return selectedRound || drawState.rounds[drawState.rounds.length - 1];
  }, [drawState, selectedRoundId]);

  useEffect(() => {
    if (!drawState?.rounds?.length) {
      setSelectedRoundId("");
      return;
    }

    setSelectedRoundId((current) => {
      const currentRoundStillExists = drawState.rounds.some(
        (round) => String(round.round_id) === String(current),
      );
      if (current && currentRoundStillExists) return current;

      const firstUnfinishedRound = [...drawState.rounds]
        .sort((a, b) => a.round_id - b.round_id)
        .find(
          (round) =>
            round.status === "open" &&
            !round.auto_generated &&
            (round.picks?.length || 0) < drawState.team_count,
        );
      return String(
        firstUnfinishedRound?.round_id ||
          drawState.rounds[drawState.rounds.length - 1].round_id,
      );
    });
  }, [drawState]);

  const openRound = currentRound?.status === "open" ? currentRound : null;
  const pickRound = openRound && !openRound.auto_generated ? openRound : null;

  const selectedSeasonName = useMemo(
    () =>
      seasons.find((season) => String(season.id) === String(selectedSeason))
        ?.name || "",
    [seasons, selectedSeason],
  );

  const selectedLevelName = useMemo(
    () =>
      formatSerieLabel(
        levels.find((level) => String(level.id) === String(selectedLevel))?.name ||
          "",
      ),
    [levels, selectedLevel],
  );

  const isSerieC = useMemo(() => {
    const level = levels.find((item) => String(item.id) === String(selectedLevel));
    const levelName = String(level?.name || "").trim().toLowerCase();
    return Number(selectedLevel) === 4 || levelName === "serie c" || levelName === "série c";
  }, [levels, selectedLevel]);

  const selectedTeam = useMemo(
    () =>
      drawState?.teams?.find((team) => String(team.id) === String(selectedTeamId)) ||
      null,
    [drawState, selectedTeamId],
  );

  const managerPick = useMemo(() => {
    if (!pickRound || !user?.teamId) return null;
    return pickRound.picks.find(
      (pick) => String(pick.team_id) === String(user.teamId),
    );
  }, [pickRound, user?.teamId]);

  const matchCount = useMemo(
    () =>
      (drawState?.rounds || []).reduce(
        (sum, round) => sum + (round.matches?.length || 0),
        0,
      ),
    [drawState],
  );

  const canCreateRound =
    isAdmin &&
    drawState &&
    drawState.status !== "completed" &&
    !drawState.rounds?.some((round) => round.status === "open");

  const handleInit = async () => {
    setActionLoading("init");
    setError("");
    setNotice("");
    try {
      const res = await axios.post(`${API_URL}/schedules/draw/init`, {
        season_id: selectedSeason,
        level_team_id: selectedLevel,
      });
      setDrawState(res.data);
      setStateMissing(false);
      setNotice("Đã khởi tạo đợt bốc thăm.");
    } catch (err) {
      setError(getErrorMessage(err, "Không thể khởi tạo bốc thăm."));
    } finally {
      setActionLoading("");
    }
  };

  const handleCreateRound = async (event) => {
    event.preventDefault();
    setActionLoading("round");
    setError("");
    setNotice("");
    setAvailableNumbers(null);
    setLastPick(null);
    try {
      const res = await axios.post(`${API_URL}/schedules/draw/rounds`, {
        season_id: selectedSeason,
        level_team_id: selectedLevel,
        play_date: playDate,
        allow_two_round_ahead: allowTwoRoundAhead,
      });
      setDrawState(res.data);
      const createdRound = res.data.rounds?.[res.data.rounds.length - 1];
      if (createdRound?.auto_generated) {
        setNotice("Đã tạo vòng cuối. Bấm Chốt vòng để tạo lịch.");
      } else if (createdRound?.duty_override?.enabled) {
        setNotice(`Đã tạo vòng ${createdRound?.round_id || ""} với chế độ bốc trước Serie C.`);
      } else {
        setNotice(`Đã tạo vòng ${createdRound?.round_id || ""}.`);
      }
      setAllowTwoRoundAhead(false);
    } catch (err) {
      setError(getErrorMessage(err, "Không thể tạo vòng mới."));
    } finally {
      setActionLoading("");
    }
  };

  const handlePick = async (teamId) => {
    setActionLoading(`pick-${teamId || "manager"}`);
    setError("");
    setNotice("");
    setLastPick(null);
    try {
      const payload = {
        season_id: selectedSeason,
        level_team_id: selectedLevel,
      };
      if (isAdmin) payload.team_id = teamId;
      const res = await axios.post(`${API_URL}/schedules/draw/pick`, payload);
      setDrawState(res.data.state);
      setLastPick(res.data.pick);
      setAvailableNumbers(null);
    } catch (err) {
      setError(getErrorMessage(err, "Không thể bốc số."));
    } finally {
      setActionLoading("");
    }
  };

  const handleCloseRound = async () => {
    setActionLoading("close");
    setError("");
    setNotice("");
    try {
      const res = await axios.post(`${API_URL}/schedules/draw/close-round`, {
        season_id: selectedSeason,
        level_team_id: selectedLevel,
      });
      setDrawState(res.data);
      setNotice(
        openRound?.auto_generated
          ? "Đã chốt vòng cuối và tạo lịch."
          : `Đã chốt vòng ${openRound?.round_id || ""}.`,
      );
      setAvailableNumbers(null);
    } catch (err) {
      setError(getErrorMessage(err, "Không thể chốt vòng."));
    } finally {
      setActionLoading("");
    }
  };

  const handleAvailableNumbers = async () => {
    if (!selectedTeamId) return;
    setActionLoading("available");
    setError("");
    try {
      const res = await axios.get(`${API_URL}/schedules/draw/available-numbers`, {
        params: {
          season_id: selectedSeason,
          level_team_id: selectedLevel,
          team_id: selectedTeamId,
        },
      });
      setAvailableNumbers(res.data);
    } catch (err) {
      setError(getErrorMessage(err, "Không thể lấy số hợp lệ."));
    } finally {
      setActionLoading("");
    }
  };

  const handleGrantDuty = async (teamId) => {
    const reason = window.prompt(
      "Lý do cấp quyền bốc ngoại lệ",
      "Thiếu người/bỏ trận ở vòng trước",
    );
    if (reason === null) return;

    setActionLoading(`grant-${teamId}`);
    setError("");
    setNotice("");
    try {
      const res = await axios.post(`${API_URL}/schedules/draw/grant-duty`, {
        season_id: selectedSeason,
        level_team_id: selectedLevel,
        team_id: teamId,
        reason,
      });
      setDrawState(res.data);
      setNotice("Đã cấp quyền bốc ngoại lệ cho đội.");
    } catch (err) {
      setError(getErrorMessage(err, "Không thể cấp quyền bốc ngoại lệ."));
    } finally {
      setActionLoading("");
    }
  };

  const teamPickStatus = (teamId) => {
    if (!currentRound) return null;
    return currentRound.picks.find((pick) => String(pick.team_id) === String(teamId));
  };

  if (!isAdmin && !isManager) {
    return (
      <Container fluid className="draw-page py-4">
        <Alert variant="warning">Tài khoản này không có quyền bốc thăm.</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="draw-page py-4">
      <section className="draw-hero">
        <div>
          <div className="draw-kicker">Admin / Bốc thăm</div>
          <h2>Bốc thăm theo vòng</h2>
          <p>
            Chọn mùa và serie, mở từng vòng bằng ngày thi đấu, sau đó bốc số
            trực tiếp cho từng đội.
          </p>
          <div className="draw-context">
            <span>
              <FiCalendar />
              {selectedSeasonName || "Mùa giải"}
            </span>
            <span>
              <FiGrid />
              {selectedLevelName || "Série"}
            </span>
            {drawState && (
              <span>
                <FiUsers />
                {drawState.team_count} đội
              </span>
            )}
          </div>
        </div>
        <Button
          className="draw-secondary-btn"
          onClick={fetchState}
          disabled={loading || !selectedSeason || !selectedLevel}
        >
          {loading ? <Spinner animation="border" size="sm" /> : <FiRefreshCw />}
          Làm mới
        </Button>
      </section>

      <section className="draw-filter-panel">
        <Form.Group>
          <Form.Label>Mùa giải</Form.Label>
          <Form.Select
            value={selectedSeason}
            onChange={(event) => setSelectedSeason(event.target.value)}
            className="draw-select"
          >
            {seasons.map((season) => (
              <option key={season.id} value={season.id}>
                {season.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group>
          <Form.Label>Série</Form.Label>
          <Form.Select
            value={selectedLevel}
            onChange={(event) => setSelectedLevel(event.target.value)}
            className="draw-select"
            disabled={isManager}
          >
            {levels.map((level) => (
              <option key={level.id} value={level.id}>
                {formatSerieLabel(level.name)}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        {isAdmin && stateMissing && (
          <Button
            className="draw-primary-btn"
            onClick={handleInit}
            disabled={actionLoading === "init" || !selectedSeason || !selectedLevel}
          >
            {actionLoading === "init" ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <FiPlay />
            )}
            Khởi tạo
          </Button>
        )}
      </section>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert variant="success" dismissible onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}
      {lastPick && (
        <section className="draw-reveal">
          <FiShuffle />
          <div>
            <span>{lastPick.team_name}</span>
            <strong>Số {lastPick.draw_number}</strong>
          </div>
        </section>
      )}

      {loading && !drawState ? (
        <section className="draw-empty">
          <Spinner animation="border" />
          <span>Đang tải bốc thăm...</span>
        </section>
      ) : stateMissing ? (
        <section className="draw-empty">
          <FiShuffle />
          <strong>Chưa khởi tạo bốc thăm cho serie này.</strong>
          <span>Admin cần khởi tạo trước khi tạo vòng.</span>
        </section>
      ) : drawState ? (
        <>
          <section className="draw-stat-grid">
            <article className="draw-stat-card">
              <div className="draw-stat-icon">
                <FiUsers />
              </div>
              <div>
                <span>Số đội</span>
                <strong>{drawState.team_count}</strong>
              </div>
            </article>
            <article className="draw-stat-card">
              <div className="draw-stat-icon blue">
                <FiGrid />
              </div>
              <div>
                <span>Lá thăm mỗi vòng</span>
                <strong>{drawState.max_draw_number}</strong>
              </div>
            </article>
            <article className="draw-stat-card">
              <div className="draw-stat-icon violet">
                <FiClock />
              </div>
              <div>
                <span>Vòng hiện tại</span>
                <strong>
                  {currentRound ? `${currentRound.round_id}/${drawState.total_rounds}` : "0"}
                </strong>
              </div>
            </article>
            <article className="draw-stat-card">
              <div className="draw-stat-icon amber">
                <FiZap />
              </div>
              <div>
                <span>Trận đã tạo</span>
                <strong>{matchCount}</strong>
              </div>
            </article>
          </section>

          {isAdmin && (
            <section className="draw-control-panel">
              <Form onSubmit={handleCreateRound} className="draw-round-form">
                <Form.Group>
                  <Form.Label>Ngày thi đấu vòng mới</Form.Label>
                  <Form.Control
                    type="date"
                    value={playDate}
                    onChange={(event) => setPlayDate(event.target.value)}
                    className="draw-input"
                  />
                </Form.Group>
                {isSerieC && (
                  <Form.Group className="draw-override-check">
                    <Form.Check
                      type="checkbox"
                      id="allow-two-round-ahead"
                      label="Bốc trước 2 vòng Serie C"
                      checked={allowTwoRoundAhead}
                      onChange={(event) => setAllowTwoRoundAhead(event.target.checked)}
                      disabled={!canCreateRound || actionLoading === "round"}
                    />
                    <Form.Text>
                      Dùng khi Serie C cần bốc vòng kế tiếp dù vòng liền trước chưa hoàn thành.
                    </Form.Text>
                  </Form.Group>
                )}
                <Button
                  type="submit"
                  className="draw-primary-btn"
                  disabled={!canCreateRound || actionLoading === "round"}
                >
                  {actionLoading === "round" ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <FiCalendar />
                  )}
                  Tạo vòng mới
                </Button>
              </Form>
              {openRound && (
                <Button
                  className="draw-success-btn"
                  onClick={handleCloseRound}
                  disabled={actionLoading === "close"}
                >
                  {actionLoading === "close" ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <FiCheckCircle />
                  )}
                  {openRound.auto_generated
                    ? "Tạo lịch vòng cuối"
                    : `Chốt vòng ${openRound.round_id}`}
                </Button>
              )}
            </section>
          )}

          {currentRound ? (
            <section className="draw-workspace">
              <div className="draw-panel-header">
                <div>
                  <h3>
                    Vòng {currentRound.round_id}
                    {currentRound.auto_generated && " - Vòng cuối"}
                  </h3>
                  <p>
                    Ngày {formatDate(currentRound.play_date)} ·{" "}
                    {currentRound.status === "open"
                      ? currentRound.auto_generated
                        ? "Chờ chốt"
                        : "Đang bốc"
                      : "Đã chốt"}
                  </p>
                </div>
                <div className="draw-round-tools">
                  <Form.Select
                    value={selectedRoundId || currentRound.round_id}
                    onChange={(event) => {
                      setSelectedRoundId(event.target.value);
                      setAvailableNumbers(null);
                    }}
                    className="draw-round-select"
                    aria-label="Chọn vòng bốc thăm"
                  >
                    {drawState.rounds.map((round) => (
                      <option key={round.round_id} value={round.round_id}>
                        Vòng {round.round_id} -{" "}
                        {round.auto_generated
                          ? round.status === "open"
                            ? "Chờ chốt"
                            : "Vòng cuối"
                          : round.status === "open"
                            ? `${round.picks.length}/${drawState.team_count} đã bốc`
                            : "Đã chốt"}
                      </option>
                    ))}
                  </Form.Select>
                  <Badge bg={currentRound.status === "open" ? "warning" : "success"}>
                    {currentRound.status === "open" ? "Đang mở" : "Đã đóng"}
                  </Badge>
                </div>
              </div>

              {currentRound.auto_generated && (
                <Alert variant="info" className="draw-inline-alert">
                  {currentRound.status === "open"
                    ? "Vòng cuối không bốc số vì chỉ còn các cặp chưa gặp. Bấm Chốt vòng để tạo lịch tự động."
                    : "Vòng cuối không bốc số vì chỉ còn các cặp chưa gặp. Lịch đã được tạo tự động."}
                </Alert>
              )}

              {currentRound.duty_override?.enabled && (
                <Alert variant="warning" className="draw-inline-alert">
                  Vòng này đang bật chế độ bốc trước Serie C. Hệ thống bỏ qua nhiệm vụ vòng liền trước, nhưng vẫn kiểm tra các vòng cũ hơn.
                </Alert>
              )}

              {!currentRound.auto_generated && (
                <>
                  <div className="draw-number-grid">
                    {currentRound.numbers.map((slot) => (
                      <article
                        className={`draw-number-card ${
                          slot.is_bye ? "draw-number-bye" : ""
                        }`}
                        key={slot.draw_number}
                      >
                        <div className="draw-number-head">
                          <strong>Số {slot.draw_number}</strong>
                          <span>{slot.is_bye ? "Nghỉ" : `${slot.teams.length}/${slot.capacity}`}</span>
                        </div>
                        <div className="draw-number-teams">
                          {slot.teams.length > 0 ? (
                            slot.teams.map((team) => (
                              <span key={team.id}>{team.name}</span>
                            ))
                          ) : (
                            <em>Chưa có đội</em>
                          )}
                        </div>
                      </article>
                    ))}
                  </div>

                  <Row className="g-3">
                    <Col lg={isAdmin ? 7 : 12}>
                      <section className="draw-team-panel">
                        <div className="draw-section-title">
                          <h4>Danh sách đội</h4>
                          <span>{currentRound.picks.length}/{drawState.team_count} đã bốc</span>
                        </div>
                        <div className="draw-team-list">
                          {drawState.teams.map((team) => {
                            const pick = teamPickStatus(team.id);
                            const grant = currentRound.duty_grants?.[String(team.id)];
                            const isOwnTeam = String(team.id) === String(user?.teamId);
                            const canAdminPick = isAdmin && pickRound && !pick;
                            const canManagerPick =
                              isManager && pickRound && isOwnTeam && !managerPick;
                            return (
                              <article
                                className={`draw-team-row ${isOwnTeam ? "own" : ""}`}
                                key={team.id}
                              >
                                <div>
                                  <strong>{team.name}</strong>
                                  <span>
                                    {pick
                                      ? `Đã bốc số ${pick.draw_number}`
                                      : grant
                                        ? "Đã được cấp quyền bốc"
                                        : "Chưa bốc"}
                                  </span>
                                </div>
                                {pick ? (
                                  <Badge bg="success">Số {pick.draw_number}</Badge>
                                ) : canAdminPick ? (
                                  <div className="draw-admin-actions">
                                    {grant ? (
                                      <Badge bg="warning">Đã cấp quyền</Badge>
                                    ) : (
                                      <Button
                                        className="draw-secondary-btn"
                                        onClick={() => handleGrantDuty(team.id)}
                                        disabled={actionLoading === `grant-${team.id}`}
                                      >
                                        {actionLoading === `grant-${team.id}` ? (
                                          <Spinner animation="border" size="sm" />
                                        ) : (
                                          <FiCheckCircle />
                                        )}
                                        Cấp quyền
                                      </Button>
                                    )}
                                    <Button
                                      className="draw-secondary-btn"
                                      onClick={() => handlePick(team.id)}
                                      disabled={actionLoading === `pick-${team.id}`}
                                    >
                                      {actionLoading === `pick-${team.id}` ? (
                                        <Spinner animation="border" size="sm" />
                                      ) : (
                                        <FiShuffle />
                                      )}
                                      Bốc thay
                                    </Button>
                                  </div>
                                ) : canManagerPick ? (
                                  <Button
                                    className="draw-primary-btn"
                                    onClick={() => handlePick(team.id)}
                                    disabled={actionLoading === `pick-${team.id}`}
                                  >
                                    {actionLoading === `pick-${team.id}` ? (
                                      <Spinner animation="border" size="sm" />
                                    ) : (
                                      <FiShuffle />
                                    )}
                                    Bốc số ngay
                                  </Button>
                                ) : (
                                  <Badge bg="secondary">Chờ</Badge>
                                )}
                              </article>
                            );
                          })}
                        </div>
                      </section>
                    </Col>

                    {isAdmin && (
                      <Col lg={5}>
                        <section className="draw-admin-panel">
                          <div className="draw-section-title">
                            <h4>Bốc thay đội</h4>
                            <span>Admin</span>
                          </div>
                          <Form.Group className="mb-3">
                            <Form.Label>Đội cần bốc</Form.Label>
                            <Form.Select
                              value={selectedTeamId}
                              onChange={(event) => {
                                setSelectedTeamId(event.target.value);
                                setAvailableNumbers(null);
                              }}
                              className="draw-select"
                            >
                              {drawState.teams.map((team) => (
                                <option key={team.id} value={team.id}>
                                  {team.name}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                          <div className="draw-admin-actions">
                            <Button
                              className="draw-primary-btn"
                              onClick={() => handlePick(selectedTeamId)}
                              disabled={
                                !pickRound ||
                                !selectedTeam ||
                                actionLoading === `pick-${selectedTeamId}`
                              }
                            >
                              {actionLoading === `pick-${selectedTeamId}` ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <FiShuffle />
                              )}
                              Bốc số ngay
                            </Button>
                            <Button
                              className="draw-secondary-btn"
                              onClick={handleAvailableNumbers}
                              disabled={!pickRound || actionLoading === "available"}
                            >
                              {actionLoading === "available" ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <FiEye />
                              )}
                              Xem số hợp lệ
                            </Button>
                          </div>
                          {availableNumbers && (
                            <div className="draw-available">
                              <span>Số hợp lệ cho {availableNumbers.team_name}</span>
                              <div>
                                {availableNumbers.numbers.length > 0 ? (
                                  availableNumbers.numbers.map((number) => (
                                    <strong key={number}>Số {number}</strong>
                                  ))
                                ) : (
                                  <em>Không còn số hợp lệ</em>
                                )}
                              </div>
                            </div>
                          )}
                          {selectedTeam && pickRound && !teamPickStatus(selectedTeam.id) && (
                            <Button
                              className="draw-secondary-btn mt-3"
                              onClick={() => handleGrantDuty(selectedTeam.id)}
                              disabled={actionLoading === `grant-${selectedTeam.id}`}
                            >
                              {actionLoading === `grant-${selectedTeam.id}` ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <FiCheckCircle />
                              )}
                              Cấp quyền bốc ngoại lệ
                            </Button>
                          )}
                        </section>
                      </Col>
                    )}
                  </Row>
                </>
              )}
            </section>
          ) : (
            <section className="draw-empty">
              <FiCalendar />
              <strong>Chưa có vòng đang mở.</strong>
              <span>Admin tạo vòng mới bằng ngày thi đấu để bắt đầu bốc số.</span>
            </section>
          )}

          <section className="draw-log-panel">
            <div className="draw-section-title">
              <h4>Nhật ký bốc thăm</h4>
              <span>{drawState.logs.length} dòng gần nhất</span>
            </div>
            <div className="draw-log-list">
              {drawState.logs.length > 0 ? (
                [...drawState.logs].reverse().slice(0, 40).map((log) => (
                  <article key={log.id}>
                    <span>{new Date(log.time).toLocaleString("vi-VN")}</span>
                    <strong>{log.message}</strong>
                  </article>
                ))
              ) : (
                <div className="draw-log-empty">Chưa có nhật ký.</div>
              )}
            </div>
          </section>
        </>
      ) : (
        <section className="draw-empty">
          <FiShuffle />
          <strong>Chọn mùa và serie để xem bốc thăm.</strong>
        </section>
      )}
    </Container>
  );
};

export default DrawManagement;
